import {
    createContext,
    useContext,
    useReducer,
    useEffect,
    ReactNode,
    useCallback
} from 'react';

import {
    SearchState,
    SearchActionTypes        
} from './types';

import { initialState, searchReducer } from './searchReducer';
import { useFindAllTalentsLazyQuery, useSaveSearchHistoryMutation } from '~graphqlResources';
import useOnGraphQLErrors from '~hooks/useOnGraphQLErrors';



type SearchContextType = {
  state: SearchState;
  loading: boolean;  
  refetchData: (overridePrompt: string) => void;
  setPromptsHistory: (history: string[]) => void;
  setResultsData: (data: object) => void;
  setShowResults: (value: boolean) => void;
  resetSearchContext: () => void;
  initializePromptHistory: (history: string[]) => void;
};

const SearchContext = createContext<SearchContextType>({
    state: initialState,
    loading: false,   
    refetchData: () => {},
    setPromptsHistory: () => {},
    setResultsData: () => {},
    setShowResults: () => {},
    resetSearchContext: () => {},
    initializePromptHistory: () => {}
});



export const SearchProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(searchReducer, initialState);

    const handleGraphQLError = useOnGraphQLErrors();

    const [fetchTalents, { data, loading}] = useFindAllTalentsLazyQuery({
        onError: handleGraphQLError()
    });    

    const [saveSearchHistory] = useSaveSearchHistoryMutation({
        onError: handleGraphQLError()        
    });

    const setResultsData = useCallback(function(data: object) {
        dispatch({type: SearchActionTypes.SET_DATA, payload: data});
    },[dispatch]);

    
    
    useEffect(()=>{
        if (data){
            setResultsData(data);
        }
    },[data, setResultsData]);
    
    function refetchData(promptToUse: string){        
        if (!promptToUse) return;
        if ( promptToUse === state.promptHistory[0] && data?.findAllTalents?.results?.length > 0)
            return;
        setResultsData({});
        fetchTalents({
            variables: {
                query: promptToUse,
                pagination: {
                    limit: 100,
                    page: 1
                }
            }
        });
    }

    const setPromptsHistory = useCallback(
        async (history: string[]) => {
            try {
                const res = await saveSearchHistory({
                    variables: {
                        searchHistory: history
                    }
                });
            
                if (res.data?.saveSearchHistory?.success) {
                    dispatch({
                        type: SearchActionTypes.ADD_PROMPT_HISTORY,
                        payload: history
                    });
                }
            } catch (e) {
                //console.log('something went wrong');
            }
        },
        [saveSearchHistory]
    );

    function setShowResults(value: boolean) {
        dispatch({type: SearchActionTypes.TOGGLE_RESULTS, payload: value});
    }

    function resetSearchContext() {
        dispatch({type: SearchActionTypes.RESET});
    }  

    function initializePromptHistory(history: string[]) {
        dispatch({
            type: SearchActionTypes.ADD_PROMPT_HISTORY,
            payload: history
        });
    }
   

    return (
        <SearchContext.Provider value={{ state, loading, refetchData, setPromptsHistory, setResultsData, setShowResults, resetSearchContext, initializePromptHistory}}>
            {children}
        </SearchContext.Provider>
    );
};

export const useSearchContext = () => useContext(SearchContext);
